import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Empty, Modal, Segmented, Select, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle, ArrowDownRight, ArrowRight, ArrowUpRight, ClipboardCheck, Database, Hourglass, PackageX, TrendingUp, Truck, Wallet,
} from 'lucide-react';
import Layout from '../../Layout';
import { khoApi, useKho } from './khoStore';
import {
  DOC_TYPE, docValue, expiryAlerts, fmtMoney, fmtNum, fmtQty, hasControlled, inTransit, itemById, reorderQty, serviceUsage, supplierById,
  validateDoc, whById, type StockDoc,
} from './khoLogic';
import {
  abcAnalysis, countStatus, expiryBuckets, flowSeries, pctChange, periodFlows, slowMoving, stockCover, stockMix, stockValueAt, supplierInsights,
} from './khoInsights';
import { docParty } from './Docs';
import { CreateDocButton, ExpiryText, ItemFlags, PageHeader, docUrl, fmtDate, fmtDateTime, itemUrl, newDocUrl, useKhoPerms } from './khoUi';

type Tab = 'low' | 'expiry' | 'pending' | 'transit';
type Ana = 'abc' | 'slow' | 'supplier';
const D = 86400000;
const WEEKDAYS = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
const PERIODS = { '7': 7, '30': 30, '90': 90 } as const;
/** Sắp cạn theo tốc độ dùng: còn đủ dùng ít hơn số ngày này thì đưa vào cảnh báo */
const COVER_ALERT_DAYS = 14;
const KIND_COLORS: Record<string, string> = { drug: 'bg-violet-500', consumable: 'bg-sky-500', cosmetic: 'bg-pink-500', tip: 'bg-amber-500', tool: 'bg-gray-400' };

/** 12.500.000 → "12,5 tr" */
const short = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e9) return `${fmtNum(Math.round(n / 1e8) / 10)} tỷ`;
  if (a >= 1e6) return `${fmtNum(Math.round(n / 1e5) / 10)} tr`;
  if (a >= 1e3) return `${fmtNum(Math.round(n / 1e3))}k`;
  return fmtNum(Math.round(n));
};

/** Mũi tên tăng/giảm so với kỳ trước; good = chiều tăng là tốt */
function Delta({ pct, good = 'up', suffix = 'so với kỳ trước', empty = 'kỳ trước chưa có số liệu' }: { pct: number | null; good?: 'up' | 'down' | 'none'; suffix?: string; empty?: string }) {
  if (pct === null) return <span className="text-gray-400">{empty}</span>;
  if (pct === 0) return <span className="text-gray-500">không đổi {suffix}</span>;
  const up = pct > 0;
  // Tăng quá mạnh thì đọc theo "gấp mấy lần" cho dễ hiểu
  const text = pct >= 200 ? `gấp ${fmtNum(Math.round((pct + 100) / 10) / 10)} lần` : `${Math.abs(pct)}%`;
  const tone = good === 'none' ? 'text-gray-600' : (up === (good === 'up')) ? 'text-emerald-600' : 'text-rose-600';
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return <span className={`inline-flex items-center gap-0.5 font-medium ${tone}`}><Icon size={12} />{text} <span className="font-normal text-gray-400">{suffix}</span></span>;
}

function Metric({ icon, label, value, hint, tone, onClick, title }: { icon: React.ReactNode; label: string; value: React.ReactNode; hint?: React.ReactNode; tone: string; onClick?: () => void; title?: string }) {
  const body = (
    <button type="button" onClick={onClick} className={`w-full h-full text-left bg-white dark:bg-gray-900 p-4 flex items-start gap-3 transition min-w-0 ${onClick ? 'hover:bg-gray-50 dark:hover:bg-gray-800/60' : 'cursor-default'}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight whitespace-nowrap">{value}</div>
        {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
      </div>
    </button>
  );
  return title ? <Tooltip title={title}>{body}</Tooltip> : body;
}

const Card = ({ title, extra, children, className = '' }: { title: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; className?: string }) => (
  <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm min-w-0 ${className}`}>
    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-between gap-2">
      <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{title}</div>
      {extra}
    </div>
    {children}
  </div>
);

const Row = ({ onOpen, children, actions }: { onOpen: () => void; children: React.ReactNode; actions?: React.ReactNode }) => (
  <div className="flex items-center gap-3 px-4 py-2.5 border-t border-gray-100 dark:border-gray-800 first:border-t-0 hover:bg-gray-50/70 dark:hover:bg-gray-800/40">
    <button type="button" onClick={onOpen} className="flex-1 min-w-0 text-left">{children}</button>
    {actions && <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>{actions}</div>}
  </div>
);

/** Tổng quan kho cho quản trị: chỉ số theo kỳ, xu hướng, việc cần xử lý, phân tích tồn kho */
export default function KhoDashboard() {
  const s = useKho();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canCreate, canApprove, me } = useKhoPerms();
  const now = Date.now();
  const period = (params.get('period') as keyof typeof PERIODS) || '30';
  const days = PERIODS[period] || 30;
  const wh = params.get('wh') || undefined;
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const from = now - days * D;
  const prevFrom = from - days * D;

  const data = useMemo(() => {
    const cur = periodFlows(s, from, now, wh);
    const prev = periodFlows(s, prevFrom, from - 1, wh);
    const value = stockValueAt(s, now + 1, wh);
    const valueStart = stockValueAt(s, from, wh);
    const cover = stockCover(s, now, wh);
    return {
      cur, prev, value, valueStart, cover,
      series: flowSeries(s, from, now, wh),
      mix: stockMix(s, wh),
      buckets: expiryBuckets(s, now, wh),
      slow: slowMoving(s, now, wh),
      abc: abcAnalysis(s, from, now, wh),
      sup: supplierInsights(s, from, now),
      counts: countStatus(s, now),
      usage: serviceUsage(s, from, now),
      alerts: expiryAlerts(s, now).filter(a => !wh || a.warehouseId === wh),
    };
  }, [s, from, wh]); // eslint-disable-line react-hooks/exhaustive-deps

  const { cur, prev, value, valueStart, cover, series, mix, buckets, slow, abc, sup, counts, usage, alerts } = data;
  const expired = alerts.filter(a => a.level === 'expired');
  // Cần đặt hàng: dưới tồn tối thiểu HOẶC theo tốc độ dùng sẽ cạn trong 14 ngày
  const low = cover.filter(r => r.low || (r.days !== null && r.days <= COVER_ALERT_DAYS))
    .sort((a, b) => (a.days ?? 9999) - (b.days ?? 9999));
  const pending = s.docs.filter(d => d.status === 'pending' && (!wh || d.warehouseId === wh || d.toWarehouseId === wh)).sort((a, b) => a.createdAt - b.createdAt);
  const transit = s.docs.filter(d => inTransit(d) && (!wh || d.warehouseId === wh || d.toWarehouseId === wh));
  const doi = cur.used > 0 ? Math.round(value / (cur.used / days)) : null;
  const margin = cur.saleRevenue ? Math.round(((cur.saleRevenue - cur.sale) / cur.saleRevenue) * 100) : null;
  const usageCost = usage.reduce((t, u) => t + u.actualCost, 0);
  const usageNorm = usage.reduce((t, u) => t + u.normCost, 0);
  const overs = usage.flatMap(u => u.byPractitioner.filter(p => p.normCost && p.actualCost > p.normCost * 1.1).map(p => ({ service: u.service, ...p })))
    .sort((a, b) => (b.actualCost - b.normCost) - (a.actualCost - a.normCost));
  const mixTotal = mix.kinds.reduce((a, k) => a + k.value, 0);
  const bucketTotal = buckets.reduce((a, b) => a + b.value, 0);
  const slowValue = slow.reduce((a, r) => a + r.value, 0);
  const overdueCounts = counts.filter(c => c.overdue);

  const counts4: Record<Tab, number> = { low: low.length, expiry: alerts.length, pending: pending.length, transit: transit.length };
  const firstHot = (['pending', 'expiry', 'low', 'transit'] as Tab[]).find(t => counts4[t]) || 'low';
  const tab = (params.get('tab') as Tab) || firstHot;
  const ana = (params.get('ana') as Ana) || 'abc';

  const approve = (d: StockDoc) => {
    const r = khoApi.approveDoc(d.id, me);
    if (r.ok) message.success(`Đã duyệt ${d.code}`);
    else Modal.error({ title: 'Chưa duyệt được', content: r.errors?.join('\n') });
  };
  const label = (t: Tab, text: string, hot: boolean) => (
    <span className="px-1">{text} <span className={`ml-1 text-[11px] px-1.5 rounded-full ${hot && counts4[t] ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>{counts4[t]}</span></span>
  );
  const scopeName = wh ? whById(s, wh)?.name : 'tất cả kho';

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Tổng quan kho"
          desc={`${WEEKDAYS[dayjs(now).day()]}, ${dayjs(now).format('DD/MM/YYYY')} · số liệu ${days} ngày qua của ${scopeName}, so với ${days} ngày trước đó`}
          extra={<>
            <Segmented value={period} onChange={v => go({ period: v === '30' ? null : String(v) })}
              options={[{ value: '7', label: '7 ngày' }, { value: '30', label: '30 ngày' }, { value: '90', label: '90 ngày' }]} />
            <Select className="w-[210px]" value={wh || ''} onChange={v => go({ wh: v || null })}
              options={[{ value: '', label: 'Tất cả kho' }, ...s.warehouses.map(w => ({ value: w.id, label: w.name }))]} />
            <Dropdown trigger={['click']} menu={{
              items: [{ key: 'reload', label: 'Nạp lại dữ liệu demo' }],
              onClick: () => Modal.confirm({
                title: 'Nạp lại dữ liệu demo kho?', content: 'Toàn bộ phiếu và danh mục kho trên trình duyệt này sẽ được thay bằng dữ liệu mẫu mới.',
                okText: 'Nạp lại', cancelText: 'Đóng', onOk: () => { khoApi.loadDemo(me); message.success('Đã nạp lại dữ liệu demo'); },
              }),
            }}>
              <Button aria-label="Dữ liệu demo" icon={<Database size={14} />} />
            </Dropdown>
            <CreateDocButton />
          </>} />

        {expired.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-900 px-4 py-2.5 text-sm text-rose-700">
            <AlertTriangle size={16} />
            <b>{expired.length} lô đã hết hạn còn trong kho</b>
            <span className="text-rose-600/80">— cách ly, không dùng cho khách và lập phiếu hủy:</span>
            {expired.slice(0, 3).map(a => (
              <button key={a.key} type="button" onClick={() => canCreate && navigate(newDocUrl('dispose', `wh=${a.warehouseId}&item=${a.itemId}&lot=${encodeURIComponent(a.lotNo)}&reason=${encodeURIComponent('Hết hạn sử dụng')}`))}
                className="bg-white/80 dark:bg-gray-900 border border-rose-200 rounded-full px-2 py-0.5 text-xs hover:border-rose-400">
                {itemById(s, a.itemId)?.name} · lô {a.lotNo} · {whById(s, a.warehouseId)?.name} · {fmtMoney(a.value)}
              </button>
            ))}
          </div>
        )}

        {/* ===== Chỉ số chính ===== */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-gray-200 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm">
          <Metric icon={<Wallet size={18} />} tone="bg-emerald-50 text-emerald-600" label="Giá trị tồn kho" value={short(value)} onClick={() => navigate(wh ? `/warehouses?wh=${wh}` : '/warehouses')}
            hint={<Delta pct={pctChange(value, valueStart)} good="none" suffix="so với đầu kỳ" empty="đầu kỳ chưa có tồn" />} title={fmtMoney(value)} />
          <Metric icon={<Truck size={18} />} tone="bg-sky-50 text-sky-600" label="Nhập mua trong kỳ" value={short(cur.imported)} title={fmtMoney(cur.imported)}
            hint={wh && !cur.imported && cur.transferIn ? <span>Nhận chuyển {short(cur.transferIn)}</span> : <Delta pct={pctChange(cur.imported, prev.imported)} good="none" />}
            onClick={() => navigate('/import-export?type=import')} />
          <Metric icon={<TrendingUp size={18} />} tone="bg-indigo-50 text-indigo-600" label="Xuất dùng (DV + bán)" value={short(cur.used)} title={`Dịch vụ ${fmtMoney(cur.service)} · Bán ${fmtMoney(cur.sale)}`}
            hint={<><Delta pct={pctChange(cur.used, prev.used)} good="none" /><div className="text-gray-400">{cur.sessions} buổi DV · bán {short(cur.sale)}</div></>}
            onClick={() => navigate('/inventory?tab=usage')} />
          <Metric icon={<Hourglass size={18} />} tone={doi !== null && doi > 120 ? 'bg-amber-50 text-amber-600' : 'bg-teal-50 text-teal-600'} label="Số ngày tồn kho"
            value={doi === null ? '—' : `${fmtNum(doi)} ngày`}
            title="Giá trị tồn ÷ giá trị xuất dùng bình quân mỗi ngày. Càng cao vốn càng nằm lâu trong kho."
            hint={doi === null ? 'Chưa có xuất dùng trong kỳ' : <span>Vòng quay ~{fmtNum(Math.round((365 / doi) * 10) / 10)} lần/năm</span>} />
          <Metric icon={<PackageX size={18} />} tone={cur.shrink ? 'bg-rose-50 text-rose-600' : 'bg-gray-50 text-gray-500'} label="Hao hụt" value={short(cur.shrink)} title={`Xuất hủy ${fmtMoney(cur.disposed)} + thiếu khi kiểm kê ${fmtMoney(cur.countLoss)}`}
            hint={<><Delta pct={pctChange(cur.shrink, prev.shrink)} good="down" /><div className="text-gray-400">{cur.used ? `${fmtNum(Math.round((cur.shrink / cur.used) * 1000) / 10)}% so với xuất dùng` : ''}</div></>}
            onClick={() => navigate('/import-export?type=dispose')} />
          <Metric icon={<Wallet size={18} />} tone="bg-pink-50 text-pink-600" label="Lãi gộp bán mỹ phẩm" value={short(cur.saleRevenue - cur.sale)} title={`Doanh thu ${fmtMoney(cur.saleRevenue)} · Giá vốn ${fmtMoney(cur.sale)}`}
            hint={margin === null ? 'Chưa bán trong kỳ' : <span>Biên lãi {margin}% · doanh thu {short(cur.saleRevenue)}</span>} />
        </div>

        {/* ===== Xu hướng + cơ cấu ===== */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 items-stretch">
          <Card title="Nhập – xuất dùng – hao hụt theo thời gian" extra={<span className="text-[11px] text-gray-400">{days <= 31 ? 'Theo ngày' : 'Theo tuần'} · giá vốn · trục trái: xuất dùng & hao hụt, trục phải: tồn kho & nhập</span>}>
            <div className="h-[260px] px-2 py-3">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                  <YAxis yAxisId="flow" tick={{ fontSize: 11 }} tickFormatter={short} width={52} />
                  <YAxis yAxisId="stock" orientation="right" tick={{ fontSize: 11 }} tickFormatter={short} width={56} />
                  <RTooltip formatter={(v: number) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="flow" dataKey="used" name="Xuất dùng" stackId="out" fill="#3e91ab" />
                  <Bar yAxisId="flow" dataKey="shrink" name="Hao hụt" stackId="out" fill="#f43f5e" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="stock" dataKey="stock" name="Giá trị tồn (trục phải)" stroke="#10b981" strokeWidth={2} dot={false} type="monotone" />
                  <Line yAxisId="stock" dataKey="imported" name="Nhập mua (trục phải)" stroke="none" legendType="circle"
                    dot={(p: { cx?: number; cy?: number; value?: number; index?: number }) => (p.value ? <circle key={p.index} cx={p.cx} cy={p.cy} r={5} fill="#60a5fa" stroke="#fff" strokeWidth={1.5} /> : <g key={p.index} />)} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Cơ cấu tồn kho" extra={<span className="text-xs text-gray-500">{fmtMoney(mixTotal)}</span>}>
            <div className="px-4 py-3 flex flex-col gap-4">
              <div>
                <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800">
                  {mix.kinds.map(k => <div key={k.kind} className={KIND_COLORS[k.kind]} style={{ width: `${(k.value / Math.max(1, mixTotal)) * 100}%` }} title={k.label} />)}
                </div>
                <div className="mt-2 grid grid-cols-1 gap-1">
                  {mix.kinds.map(k => (
                    <button key={k.kind} type="button" onClick={() => navigate(`/warehouses?kind=${k.kind}${wh ? `&wh=${wh}` : ''}`)} className="flex items-center justify-between text-xs hover:text-[#3e91ab]">
                      <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${KIND_COLORS[k.kind]}`} />{k.label}</span>
                      <span className="text-gray-500">{short(k.value)} · {Math.round((k.value / Math.max(1, mixTotal)) * 100)}%</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1">Theo kho</div>
                {mix.warehouses.map(w => {
                  const max = Math.max(1, ...mix.warehouses.map(x => x.value));
                  return (
                    <button key={w.warehouse.id} type="button" onClick={() => go({ wh: wh === w.warehouse.id ? null : w.warehouse.id })}
                      className={`w-full text-left text-xs py-1 rounded ${wh === w.warehouse.id ? 'text-[#3e91ab] font-semibold' : 'hover:text-[#3e91ab]'}`}>
                      <div className="flex justify-between"><span className="truncate">{w.warehouse.name}</span><span className="text-gray-500">{short(w.value)}</span></div>
                      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 mt-1"><div className="h-full rounded-full bg-[#3e91ab]" style={{ width: `${(w.value / max) * 100}%` }} /></div>
                    </button>
                  );
                })}
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1 flex justify-between"><span>Rủi ro hết hạn (giá trị)</span><span>{fmtMoney(bucketTotal)}</span></div>
                <div className="grid grid-cols-4 gap-1">
                  {buckets.map(b => (
                    <Tooltip key={b.key} title={`${b.lots} lô · ${fmtMoney(b.value)}`}>
                      <button type="button" onClick={() => go({ tab: 'expiry' })} className="text-left rounded-lg border border-gray-100 dark:border-gray-800 px-2 py-1.5 hover:border-gray-300">
                        <div className={`h-1 rounded-full mb-1 ${b.value ? b.tone : 'bg-gray-100'}`} />
                        <div className="text-[10px] text-gray-500 leading-tight">{b.label}</div>
                        <div className={`text-xs font-semibold ${b.value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-300'}`}>{short(b.value)}</div>
                      </button>
                    </Tooltip>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* ===== Cột trái: việc cần xử lý + phân tích · cột phải: kiểm soát ===== */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 items-start">
          <div className="flex flex-col gap-4 min-w-0">
          <Card title="Cần xử lý" extra={
            <div className="overflow-x-auto max-w-full">
              <Segmented value={tab} onChange={v => go({ tab: String(v) })} options={[
                { value: 'pending', label: label('pending', 'Chờ duyệt', true) },
                { value: 'expiry', label: label('expiry', 'Cận hạn', true) },
                { value: 'low', label: label('low', 'Cần đặt hàng', true) },
                { value: 'transit', label: label('transit', 'Đang chuyển', false) },
              ]} />
            </div>}>
            <div className="max-h-[440px] overflow-y-auto">
              {tab === 'low' && (low.length ? low.map(r => {
                const sug = reorderQty(r.item, r.qty);
                const urgent = r.days !== null && r.days <= 7;
                return (
                  <Row key={r.item.id} onOpen={() => navigate(itemUrl(r.item.id))}
                    actions={canCreate && <Button size="small" type="primary" ghost onClick={() => navigate(newDocUrl('import', `items=${r.item.id}:${sug}${r.item.supplierId ? `&supplier=${r.item.supplierId}` : ''}`))}>Lập phiếu nhập</Button>}>
                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
                      <span className="truncate">{r.item.name}</span><ItemFlags item={r.item} />
                      {r.days !== null && <span className={`text-[11px] px-1.5 rounded border ${urgent ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>đủ dùng ~{fmtNum(Math.floor(r.days))} ngày</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      Còn <b className="text-orange-600">{fmtQty(r.item, r.qty)}</b>{r.item.minStock ? ` / tối thiểu ${fmtNum(r.item.minStock)}` : ''}
                      {r.dailyUse > 0 && ` · dùng ~${fmtNum(Math.round(r.dailyUse * 10) / 10)} ${r.item.unit}/ngày`}
                      {' · '}đề xuất mua <b>{fmtQty(r.item, sug)}</b>{r.item.supplierId ? ` · ${supplierById(s, r.item.supplierId)?.name}` : ''}
                    </div>
                  </Row>
                );
              }) : <Empty className="py-8" description="Không có mặt hàng nào cần đặt thêm" />)}

              {tab === 'expiry' && (alerts.length ? alerts.map(a => {
                const item = itemById(s, a.itemId);
                const w = whById(s, a.warehouseId);
                const extra = `wh=${a.warehouseId}&item=${a.itemId}&lot=${encodeURIComponent(a.lotNo)}`;
                return (
                  <Row key={a.key} onOpen={() => navigate(itemUrl(a.itemId, `&wh=${a.warehouseId}`))}
                    actions={canCreate && (a.level === 'expired'
                      ? <Button size="small" danger onClick={() => navigate(newDocUrl('dispose', `${extra}&reason=${encodeURIComponent('Hết hạn sử dụng')}`))}>Xuất hủy</Button>
                      : w?.kind === 'central' || w?.kind === 'branch'
                        ? <Tooltip title="Chuyển về tủ thuốc phòng thủ thuật để dùng trước"><Button size="small" onClick={() => navigate(newDocUrl('transfer', extra))}>Chuyển để dùng trước</Button></Tooltip>
                        : <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Ưu tiên dùng trước</span>)}>
                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{item?.name} <span className="font-mono text-xs text-gray-400">· lô {a.lotNo}</span></div>
                    <div className="text-xs text-gray-500"><ExpiryText expiry={a.expiry} now={now} /> · {w?.name} · {fmtQty(item, a.qty)} · {fmtMoney(a.value)}</div>
                  </Row>
                );
              }) : <Empty className="py-8" description="Không có lô nào cận hạn trong 60 ngày tới" />)}

              {tab === 'pending' && (pending.length ? pending.map(d => {
                const blocked = validateDoc(s, d, now).length > 0;
                const ctrl = hasControlled(s, d);
                const age = Math.floor((now - d.createdAt) / 3600000);
                return (
                  <Row key={d.id} onOpen={() => navigate(docUrl(d.id))}
                    actions={canApprove && (blocked || ctrl
                      ? <Button size="small" onClick={() => navigate(docUrl(d.id))}>{ctrl ? 'Duyệt chuyên môn' : 'Xem lỗi'}</Button>
                      : <Button size="small" type="primary" onClick={() => approve(d)}>Duyệt</Button>)}>
                    <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                      <span className="font-medium text-[#3e91ab]">{d.code}</span> · {DOC_TYPE[d.type].label} · {docParty(s, d)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {whById(s, d.warehouseId)?.name} · {d.lines.length} dòng · {fmtMoney(docValue(d))} · {d.createdBy}
                      <span className={age >= 24 ? 'text-rose-600' : ''}> · chờ {age >= 24 ? `${Math.floor(age / 24)} ngày` : `${Math.max(1, age)} giờ`}</span>
                      {blocked && <span className="text-rose-600"> · không đủ điều kiện duyệt</span>}
                      {ctrl && <span className="text-violet-700"> · thuốc kiểm soát</span>}
                    </div>
                  </Row>
                );
              }) : <Empty className="py-8" description="Không có phiếu nào chờ duyệt" />)}

              {tab === 'transit' && (transit.length ? transit.map(d => (
                <Row key={d.id} onOpen={() => navigate(docUrl(d.id))}
                  actions={canCreate && <Button size="small" type="primary" ghost onClick={() => navigate(docUrl(d.id))}>Xác nhận nhận</Button>}>
                  <div className="text-sm text-gray-900 dark:text-gray-100 truncate"><span className="font-medium text-[#3e91ab]">{d.code}</span> · {whById(s, d.warehouseId)?.name} → {whById(s, d.toWarehouseId)?.name}</div>
                  <div className="text-xs text-gray-500">{d.lines.length} dòng · {fmtMoney(docValue(d))} · xuất lúc {fmtDateTime(d.approvedAt ?? d.date)}</div>
                </Row>
              )) : <Empty className="py-8" description="Không có hàng đang chuyển" />)}
            </div>
          </Card>

          {/* ===== Phân tích ===== */}
          <Card title="Phân tích tồn kho" extra={
            <Segmented value={ana} onChange={v => go({ ana: v === 'abc' ? null : String(v) })} options={[
              { value: 'abc', label: 'Nhóm ABC theo giá trị dùng' },
              { value: 'slow', label: `Hàng tồn đọng (${slow.length})` },
              { value: 'supplier', label: 'Nhà cung cấp & giá nhập' },
            ]} />}>
            {ana === 'abc' && (abc.length ? (
              <div className="p-4 flex flex-col gap-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {(['A', 'B', 'C'] as const).map(c => {
                    const rows = abc.filter(r => r.cls === c);
                    const tip = { A: 'Kiểm soát chặt: kiểm kê hàng tuần, duyệt xuất kỹ, đàm phán giá', B: 'Kiểm kê hàng tháng, đặt hàng theo kế hoạch', C: 'Đặt theo lô lớn, kiểm kê quý' }[c];
                    return (
                      <div key={c} className="rounded-lg border border-gray-100 dark:border-gray-800 px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-bold px-1.5 rounded ${c === 'A' ? 'bg-rose-100 text-rose-700' : c === 'B' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>Nhóm {c}</span>
                          <span className="text-xs text-gray-500">{rows.length} mặt hàng · {Math.round(rows.reduce((a, r) => a + r.share, 0) * 100)}% giá trị</span>
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1">{tip}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="text-xs text-gray-500"><tr>
                      <th className="text-left font-medium py-1.5 w-10">#</th><th className="text-left font-medium py-1.5">Mặt hàng</th>
                      <th className="text-right font-medium py-1.5">Giá trị xuất dùng</th><th className="text-left font-medium py-1.5 pl-4 w-[220px]">Tỷ trọng</th>
                      <th className="text-right font-medium py-1.5">Lũy kế</th><th className="text-center font-medium py-1.5 w-16">Nhóm</th>
                    </tr></thead>
                    <tbody>
                      {abc.slice(0, 10).map((r, i) => (
                        <tr key={r.item.id} className="border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40" onClick={() => navigate(itemUrl(r.item.id))}>
                          <td className="py-1.5 text-gray-400">{i + 1}</td>
                          <td className="py-1.5">{r.item.name}<ItemFlags item={r.item} /></td>
                          <td className="py-1.5 text-right">{fmtMoney(r.value)}</td>
                          <td className="py-1.5 pl-4"><div className="flex items-center gap-2"><div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full"><div className="h-full rounded-full bg-[#3e91ab]" style={{ width: `${r.share * 100}%` }} /></div><span className="text-xs text-gray-500 w-10 text-right">{Math.round(r.share * 100)}%</span></div></td>
                          <td className="py-1.5 text-right text-gray-500">{Math.round(r.cumulative * 100)}%</td>
                          <td className="py-1.5 text-center"><span className={`text-xs font-bold px-1.5 rounded ${r.cls === 'A' ? 'bg-rose-100 text-rose-700' : r.cls === 'B' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{r.cls}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : <Empty className="py-8" description="Chưa có xuất dùng trong kỳ" />)}

            {ana === 'slow' && (slow.length ? (
              <div className="p-4 flex flex-col gap-3">
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  <b className="text-amber-700">{fmtMoney(slowValue)}</b> vốn đang nằm ở {slow.length} mặt hàng không được dùng từ 45 ngày trở lên
                  {value ? ` (${Math.round((slowValue / value) * 100)}% giá trị tồn)` : ''}. Cân nhắc: chuyển sang chi nhánh đang dùng, đổi/trả nhà cung cấp, ngừng đặt thêm.
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="text-xs text-gray-500"><tr>
                      <th className="text-left font-medium py-1.5">Mặt hàng</th><th className="text-right font-medium py-1.5">Tồn</th>
                      <th className="text-right font-medium py-1.5">Giá trị</th><th className="text-right font-medium py-1.5">Lần dùng gần nhất</th><th className="text-right font-medium py-1.5">Không dùng</th>
                    </tr></thead>
                    <tbody>
                      {slow.map(r => (
                        <tr key={r.item.id} className="border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40" onClick={() => navigate(itemUrl(r.item.id))}>
                          <td className="py-1.5">{r.item.name}<ItemFlags item={r.item} /><div className="text-[11px] text-gray-400">{r.item.code}</div></td>
                          <td className="py-1.5 text-right">{fmtQty(r.item, r.qty)}</td>
                          <td className="py-1.5 text-right font-medium">{fmtMoney(r.value)}</td>
                          <td className="py-1.5 text-right text-gray-500">{r.lastUse ? fmtDate(r.lastUse) : 'Chưa từng dùng'}</td>
                          <td className="py-1.5 text-right"><span className={r.idleDays >= 90 ? 'text-rose-600 font-semibold' : 'text-amber-600'}>{r.idleDays} ngày</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : <Empty className="py-8" description="Không có hàng tồn đọng" />)}

            {ana === 'supplier' && (
              <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-semibold text-gray-500 mb-2">Giá trị nhập theo nhà cung cấp · {days} ngày</div>
                  {sup.suppliers.length ? sup.suppliers.map(r => (
                    <div key={r.supplier.id} className="py-1.5">
                      <div className="flex justify-between text-sm"><span className="truncate">{r.supplier.name}</span><span className="text-gray-600 whitespace-nowrap">{fmtMoney(r.value)} · {r.docs} phiếu</span></div>
                      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 mt-1"><div className="h-full rounded-full bg-sky-500" style={{ width: `${r.share * 100}%` }} /></div>
                    </div>
                  )) : <div className="text-sm text-gray-400">Không có phiếu nhập trong kỳ — đổi sang 90 ngày để xem thêm</div>}
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 mb-2">Biến động giá nhập (lần gần nhất so với lần trước)</div>
                  {sup.priceChanges.length ? sup.priceChanges.map(c => (
                    <button key={c.item.id} type="button" onClick={() => navigate(itemUrl(c.item.id))} className="w-full text-left flex items-center justify-between gap-2 py-1.5 border-t first:border-t-0 border-gray-100 dark:border-gray-800 hover:text-[#3e91ab]">
                      <div className="min-w-0">
                        <div className="text-sm truncate">{c.item.name}</div>
                        <div className="text-[11px] text-gray-400">{c.supplier?.name || '—'} · {fmtDate(c.at)} · {fmtMoney(c.prev)} → {fmtMoney(c.last)}/{c.item.unit}</div>
                      </div>
                      <span className={`text-sm font-semibold whitespace-nowrap ${c.pct > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{c.pct > 0 ? '▲' : '▼'} {fmtNum(Math.abs(c.pct))}%</span>
                    </button>
                  )) : <div className="text-sm text-gray-400">Giá nhập ổn định</div>}
                </div>
              </div>
            )}
          </Card>
          </div>

          <div className="flex flex-col gap-4">
            <Card title={`Tiêu hao so với định mức · ${days} ngày`} extra={<Link to="/inventory?tab=usage" className="text-xs text-[#3e91ab] flex items-center gap-1">Chi tiết <ArrowRight size={12} /></Link>}>
              <div className="px-4 py-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-xl font-bold">{fmtMoney(usageCost)}</span>
                  {usageNorm > 0 && (
                    <span className={`text-xs font-semibold ${usageCost > usageNorm * 1.05 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {usageCost >= usageNorm ? '+' : ''}{Math.round(((usageCost - usageNorm) / usageNorm) * 100)}% so với định mức
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-400">{usage.reduce((t, u) => t + u.sessions, 0)} buổi dịch vụ · định mức {fmtMoney(usageNorm)}</div>
                <div className="mt-3 flex flex-col gap-2">
                  {usage.slice(0, 4).map(u => {
                    const pct = u.normCost ? Math.round((u.actualCost / u.normCost) * 100) : 100;
                    return (
                      <Link key={u.service.id} to={`/service-norms?service=${u.service.id}`} className="block group">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-700 dark:text-gray-300 group-hover:text-[#3e91ab] truncate">{u.service.name} · {u.sessions} buổi</span>
                          <span className={pct > 110 ? 'text-rose-600 font-semibold' : 'text-gray-500'}>{short(u.actualCost)} · {pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 mt-1 overflow-hidden">
                          <div className={`h-full rounded-full ${pct > 110 ? 'bg-rose-500' : 'bg-[#3e91ab]'}`} style={{ width: `${Math.min(100, (u.actualCost / Math.max(1, usage[0].actualCost)) * 100)}%` }} />
                        </div>
                      </Link>
                    );
                  })}
                  {!usage.length && <span className="text-xs text-gray-400">Chưa có buổi dịch vụ trong kỳ</span>}
                </div>
                {overs.length > 0 && (
                  <div className="mt-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900 px-3 py-2">
                    <div className="text-xs font-semibold text-rose-700 flex items-center gap-1"><PackageX size={13} /> Dùng vượt định mức &gt;10%</div>
                    {overs.slice(0, 3).map(o => (
                      <div key={`${o.service.id}-${o.name}`} className="text-xs text-rose-700/90 mt-0.5">
                        {o.name} · {o.service.name}: +{Math.round(((o.actualCost - o.normCost) / o.normCost) * 100)}% ({fmtMoney(o.actualCost - o.normCost)})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <Card title="Kiểm kê định kỳ (30 ngày/lần)" extra={overdueCounts.length > 0 && <span className="text-[11px] text-rose-600 font-semibold">{overdueCounts.length} kho quá hạn</span>}>
              <div className="py-1">
                {counts.map(c => (
                  <div key={c.warehouse.id} className="flex items-center gap-3 px-4 py-2 border-t first:border-t-0 border-gray-100 dark:border-gray-800">
                    <ClipboardCheck size={15} className={c.overdue ? 'text-rose-500' : 'text-emerald-500'} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{c.warehouse.name}</div>
                      <div className="text-[11px] text-gray-500">
                        {c.lastCount === null ? <span className="text-rose-600">Chưa kiểm kê lần nào</span>
                          : <>Kiểm {fmtDate(c.lastCount)} ({c.days} ngày trước){c.accuracy !== null && <> · khớp sổ {c.accuracy}%</>}{c.diffValue ? <span className={c.diffValue < 0 ? 'text-rose-600' : 'text-emerald-600'}> · lệch {fmtMoney(c.diffValue)}</span> : null}</>}
                      </div>
                    </div>
                    {canCreate && c.overdue && <Button size="small" onClick={() => navigate(newDocUrl('count', `wh=${c.warehouse.id}`))}>Kiểm ngay</Button>}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>

      </div>
    </Layout>
  );
}
